import React, { useEffect, useState } from "react"
import classNames from "classnames"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRight,
  faGripVertical,
  faDiceFive,
  faXmark,
  faPlus,
} from "@fortawesome/free-solid-svg-icons"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { shuffle } from "lodash"

import { useNana } from "../../../hooks/useNana"
import { ID, Idea, Project } from "../../../../model/types"
import IdeaBox from "../../../components/idea_box"
import * as Commands from "../../../../model/commands/index"
import { useNavigate } from "react-router-dom"
import FuzzyIdeaFinder from "../../../components/fuzzy_idea_finder"
import Toggle from "./toggle"
import { MosaicPageState } from "../mosaic"

export default function ProjectContentCollapsed({
  project,
  setMode,
}: {
  project: Project
  setMode: (mode: "organize" | "draft") => void
}) {
  const { model, commander, hub } = useNana()

  const [ideaIds, setIdeaIds] = useState<ID[]>([])

  useEffect(() => {
    setIdeaIds(project.ideaIds)
  }, [project.id, model])

  useEffect(() => {
    const keys = [
      hub.on("model:project-updated", ({ id, fields }) => {
        if (id == project.id && fields.ideaIds) setIdeaIds(fields.ideaIds)
      }),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [project.id])

  const ideas = ideaIds.map((id) => model.fetchIdea(id))

  const onDragEnd = (result: any) => {
    const {
      destination: { index: targetIndex },
      draggableId,
    } = result

    const newIdeaIds = ideaIds.filter((id) => id != draggableId)
    newIdeaIds.splice(targetIndex, 0, draggableId)

    setIdeaIds(newIdeaIds)

    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          ideaIds: newIdeaIds,
        },
      })
    )
  }

  const randomizeOrder = () => {
    if (!project) return
    const project1 = model.getProject(project.id)
    if (!project1) return

    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project1.id,
        fields: {
          ideaIds: shuffle(project1.ideaIds),
        },
      })
    )
  }

  return (
    <>
      <FuzzyIdeaFinder
        title="Select idea to add"
        showNearbyOnOpen={false}
        onChoose={(idea: Idea) => {
          commander.exec(
            new Commands.SetProjectFieldsCommand({
              id: project.id,
              fields: {
                ideaIds: project.ideaIds.concat([idea.id]),
              },
            })
          )
        }}
      />
      <div className="mt-2 grow flex flex-col min-h-0">
        <div
          className="flex gap-2 justify-between items-center bg-gray-50 dark:bg-gray-800 p-4 border-y border-gray-200 dark:border-gray-600"
          data-testid="action-tray"
        >
          <Toggle mode={"organize"} setMode={setMode} />
          <div className="flex gap-4 items-center">
            <div
              onClick={() => randomizeOrder()}
              className={classNames(
                "hidden md:flex items-center  text-xs md:text-sm px-2 py-1 gap-2 rounded  select-none",
                {
                  "cursor-pointer text-gray-900 dark:text-gray-200 bg-sky-100 dark:bg-sky-900 hover:bg-sky-200 dark:hover:bg-sky-800 hover:shadow":
                    ideas.length > 1,
                  " cursor-default text-gray-500 dark:text-gray-300 bg-gray-200 dark:bg-gray-700":
                    ideas.length < 2,
                }
              )}
            >
              <FontAwesomeIcon
                icon={faDiceFive}
                className="text-gray-500 dark:text-gray-300 text-lg"
              />
              Randomize
            </div>
            <AddIdeaButton />
          </div>
        </div>
        {ideas.length > 0 && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided, _snapshot) => (
                // snapshot provides isDraggingOver, isDragging
                // provided provides some draggableProps.style
                <>
                  <div
                    className="p-4 grow flex flex-col overflow-y-scroll min-h-0"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    data-testid="organizer-idea-list"
                  >
                    {ideas.map((idea, index) => (
                      <Draggable
                        key={idea.id}
                        draggableId={idea.id as string}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={classNames("px-2 py-1", {})}
                          >
                            <IdeaItem
                              key={idea.id}
                              idea={idea}
                              project={project}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  </div>
                  {provided.placeholder}
                </>
              )}
            </Droppable>
          </DragDropContext>
        )}
        {ideas.length == 0 && <EmptyState />}
      </div>
    </>
  )
}

const EmptyState = () => {
  return (
    <div className="grow flex flex-col gap-4 items-center justify-start pt-4 md:pt-24 text-sm md:text-base text-gray-700 dark:text-gray-300">
      Add ideas to this project:
      <AddIdeaButton />
      <div className="mt-2">You can also add ideas from the mosaic.</div>
    </div>
  )
}

const AddIdeaButton = () => {
  return (
    <div
      onClick={() =>
        window.dispatchEvent(new CustomEvent("open-fuzzy-finder", {}))
      }
      className="flex items-center cursor-pointer text-xs md:text-sm px-2 py-1 bg-sky-100 hover:bg-sky-200 dark:bg-sky-900 gap-2 rounded hover:shadow dark:hover:bg-sky-800 dark:text-gray-100"
      data-testid="add-idea-button"
    >
      <FontAwesomeIcon icon={faPlus} />
      Add idea
    </div>
  )
}

const IdeaItem = ({ idea, project }: { idea: Idea; project: Project }) => {
  const { model, commander } = useNana()
  const navigate = useNavigate()

  const removeIdeaFromProject = (idea) => {
    commander.exec(
      new Commands.SetProjectFieldsCommand({
        id: project.id,
        fields: {
          ideaIds: project.ideaIds.filter((id) => id != idea.id),
        },
      })
    )
  }

  const clickedOnIdea = (idea: Idea) => {
    MosaicPageState.forceOpenLocation = idea.location
    navigate("/app")
  }

  return (
    <div className="relative flex gap-4 items-center justify-stretch group text-xs md:text-base">
      <div>
        <FontAwesomeIcon icon={faGripVertical} className="text-gray-400" />
      </div>
      <div>
        <IdeaBox idea={idea} />
      </div>
      <div className="grow text-gray-700 dark:text-gray-300">{idea.title}</div>
      <div className="flex md:hidden md:group-hover:flex items-center bg-white dark:bg-gray-950 md:bg-none md:dark:bg-none absolute top-0 right-0 bottom-0 my-auto md:mt-1 md:mb-0 md:static">
        <div
          className="w-7 h-7 flex items-center justify-center rounded-sm text-gray-400 dark:hover:text-gray-200 hover:bg-gray-600 cursor-pointer"
          onClick={() => clickedOnIdea(idea)}
        >
          <FontAwesomeIcon icon={faArrowRight} className="text-xl" />
        </div>
        <div
          className="w-7 h-7 flex items-center justify-center rounded-sm text-gray-400 dark:hover:text-gray-200 hover:bg-gray-600 cursor-pointer"
          onClick={() => removeIdeaFromProject(idea)}
        >
          <FontAwesomeIcon icon={faXmark} className="text-xl" />
        </div>
      </div>
    </div>
  )
}
